// Per-page SEO: title, description, canonical, Open Graph / Twitter, robots and JSON-LD.
// Static defaults live in public/index.html; this keeps them in sync on every route change.

export const SITE_URL = (process.env.VUE_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
export const SITE_NAME = 'RiSing';
export const DEFAULT_TITLE = 'RiSing - Alat Karaoke Rumah & Set Top Box Karaoke Legal';
export const DEFAULT_DESCRIPTION = 'RiSing adalah sistem karaoke rumah dari Happy Puppy Group. Set Top Box dan Console One dengan 80.000+ lagu karaoke legal, tanpa iklan, mulai Rp5.000 per hari.';
export const DEFAULT_IMAGE = `${SITE_URL}/rising_share.png`;

const JSON_LD_ID = 'page-jsonld';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content === undefined || content === null || content === '') {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data) {
  let el = document.getElementById(JSON_LD_ID);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function stripHtml(html, maxLength = 160) {
  const text = String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/[​\s]+/g, ' ')
    .trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).replace(/\s+\S*$/, '') + '…';
}

export function absoluteUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  return SITE_URL + (path.startsWith('/') ? path : `/${path}`);
}

// Builds the canonical URL from the route path, keeping only the query keys that
// identify the content (e.g. product_id), so tracking params don't create duplicates.
export function canonicalFor(route, queryKeys = []) {
  const params = queryKeys
    .filter(key => route.query[key] !== undefined && route.query[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(route.query[key])}`);
  return SITE_URL + route.path + (params.length ? `?${params.join('&')}` : '');
}

/**
 * @param {Object} options
 * @param {string} [options.title]        Page title; the brand is appended automatically
 * @param {string} [options.description]
 * @param {string} [options.canonical]    Absolute URL; omitted for noindex pages
 * @param {string} [options.image]
 * @param {string} [options.type]         og:type (website, article, product)
 * @param {boolean} [options.noindex]
 * @param {Object|Array} [options.jsonLd] Page-specific structured data
 */
export function setPageMeta(options = {}) {
  const title = options.title ? `${options.title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const description = options.description || DEFAULT_DESCRIPTION;
  const image = absoluteUrl(options.image) || DEFAULT_IMAGE;
  const canonical = options.noindex ? '' : options.canonical;

  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', options.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  upsertCanonical(canonical);

  upsertMeta('property', 'og:type', options.type || 'website');
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical || SITE_URL + window.location.pathname);
  upsertMeta('property', 'og:image', image);

  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
  upsertMeta('name', 'twitter:url', canonical || SITE_URL + window.location.pathname);

  setJsonLd(options.jsonLd);
}

// Called from router.afterEach. Routes without meta.seo are treated as private (noindex).
export function applyRouteSeo(route) {
  const seo = route.meta && route.meta.seo;
  if (!seo) {
    setPageMeta({ noindex: true });
    return;
  }
  setPageMeta({
    ...seo,
    canonical: canonicalFor(route, seo.canonicalQuery),
    jsonLd: seo.breadcrumb ? breadcrumbJsonLd(seo.breadcrumb, route) : undefined
  });
}

export function breadcrumbJsonLd(items, route) {
  const list = [{ name: 'Beranda', path: '/' }, ...items];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path ? absoluteUrl(item.path) : canonicalFor(route)
    }))
  };
}
